import { useAppSelector } from 'appState/store'
import { ChangePasswordModal } from 'components/Account/ChangePasswordModal'
import { Layout } from 'components/Layout'
import { LoadingBox } from '@outbound-ai/ursa'
import { authApi } from 'features/auth/authApi'
import { useUserProfile } from 'features/settings/settingsHooks'
import { IUserRoleGuidEnum } from 'features/settings/settingsTypes'
import { PathsEnum } from 'pages/Paths'
import { useState } from 'react'
import { FaCaretRight } from 'react-icons/fa'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import styled from 'styled-components'
import { ColorsEnum, ColorsToneEnum } from 'types/cssTypes'
import { ErrorMessagesEnum } from 'types/errorTypes'
import { notificator } from 'utils/notificator/notificator'

export const AccountSettingsPage = () => {
  const { user } = useAppSelector((state) => state.auth)
  const { data: profile, isLoading } = useUserProfile(user?.id)
  // get the tenants from the profile
  const tenants = profile?.tenants

  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false)
  const [isLoadingChangePassword, setIsLoadingChangePassword] = useState(false)
  const { pathname } = useLocation()

  const handleChangePassword = async (oldPassword: string, newPassword: string) => {
    try {
      setIsLoadingChangePassword(true)
      await authApi.handleChangePassword(oldPassword, newPassword)
      notificator.notify({ type: 'success', heading: `Password changed successfully` })
      //notificator.success(`Password changed successfully`)
    } catch (error: any) {
      notificator.notify({ type: 'error', heading: error.message || ErrorMessagesEnum.SomethingWentWrong })
      //notificator.error(error.message || ErrorMessagesEnum.SomethingWentWrong)
    } finally {
      setIsLoadingChangePassword(false)
      setShowChangePasswordModal(false)
    }
  }
  const roleIds = profile?.roles.map((role) => role.roleId)
  const isClaimAccessUser = roleIds?.includes(IUserRoleGuidEnum.claimAccessPermission)
  const isrunAutocheckUser = roleIds?.includes(IUserRoleGuidEnum.runAutoCheckPermission)
  const dataMapping = roleIds?.includes(IUserRoleGuidEnum.dataMapping)

  if (isLoading) return <LoadingBox />
  return (
    <>
      {showChangePasswordModal && (
        <ChangePasswordModal
          handleChangePassword={handleChangePassword}
          isLoadingChangePassword={isLoadingChangePassword}
          onCloseModal={() => setShowChangePasswordModal(false)}
          showOptionalButton={false}
        />
      )}
      <LayoutStyled>
        <Div>
          <h1>Settings </h1>
        </Div>
        <div className="page-main">
          <aside className="page-menu">
            <details
              open={[
                PathsEnum.accountSettingsProfile,
                PathsEnum.accountSettingsCalls,
                PathsEnum.accountSettingsDiagnostic,
              ].some((path) => pathname.includes(path))}
            >
              <summary>
                My Account{' '}
                <span>
                  <FaCaretRight />
                </span>
              </summary>
              <ul className="menu-links">
                <li>
                  <NavLink to={PathsEnum.accountSettingsProfile}>Profile</NavLink>
                </li>
                {isClaimAccessUser && (
                  <>
                    <li>
                      <NavLink to={PathsEnum.accountSettingsDiagnostic}>Diagnostics</NavLink>
                    </li>
                    <li>
                      <NavLink to={PathsEnum.accountSettingsCalls}>Calls</NavLink>
                    </li>
                  </>
                )}
              </ul>
            </details>

            {dataMapping && tenants?.length != 0 && (
              <details
                open={[
                  PathsEnum.accountSettingsColumns,
                  PathsEnum.accountSettingsSampleData,
                  PathsEnum.accountSettingsDataMaps,
                ].some((path) => pathname.includes(path))}
              >
                <summary>
                  File Center{' '}
                  <span>
                    <FaCaretRight />
                  </span>
                </summary>
                <ul className="menu-links">
                  <li>
                    <NavLink to={PathsEnum.accountSettingsColumns}>Columns</NavLink>
                  </li>
                  <li>
                    <NavLink to={PathsEnum.accountSettingsSampleData}>Sample Data</NavLink>
                  </li>
                  <li>
                    <NavLink to={PathsEnum.accountSettingsDataMaps}>Data Maps</NavLink>
                  </li>
                </ul>
              </details>
            )}

            {isrunAutocheckUser && tenants?.length != 0 && (
              <details open={[PathsEnum.accountSettingsAutoCheck].some((path) => pathname.includes(path))}>
                <summary>
                  AutoCheck{' '}
                  <span>
                    <FaCaretRight />
                  </span>
                </summary>
                <ul className="menu-links">
                  {/* Commented until the product team provides the functionality */}
                  {/* <li>
                  <NavLink to={PathsEnum.accountSettingsGroups}>Groups</NavLink>
                </li> */}

                  {/* Commented until the product team provides the functionality */}
                  {/* <li>
                  <NavLink to={PathsEnum.accountSettingsSecurity}>Security Rules</NavLink>
                </li> */}
                  {}

                  <li>
                    <NavLink to={PathsEnum.accountSettingsAutoCheck}>Settings</NavLink>
                  </li>
                </ul>
              </details>
            )}
          </aside>

          <main className="page-content">
            <Outlet context={{ setShowChangePasswordModal }} />
          </main>
        </div>
      </LayoutStyled>
    </>
  )
}

const Div = styled.div`
  height: ${({ theme }) => theme.constants.pageTitleHeight};
  display: inline-flex;
  width: 100%;
  align-items: center;
  padding: 0rem ${({ theme }) => theme.spacing.l};
  background: ${({ theme }) => theme.colors.neutral.lighter};
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey.lighter};
  margin-bottom: ${({ theme }) => theme.spacing.l};
  .refresh_holder {
    margin-left: auto;
  }
  h1 {
    ${({ theme }) => theme.typography.h4.bold};
    color: ${({ theme }) => theme.colors.primary.neutral};
    padding-block: 0.75rem;
  }

  div {
    margin-left: 0.25rem;
    float: left;
    svg {
      color: ${({ theme }) => theme.colors.grey.light};
    }
    .text {
      margin-bottom: 0.1rem;
      ${({ theme }) => theme.typography.s.normal};
    }
  }
`

const LayoutStyled = styled(Layout)`
  .page-main {
    max-width: 96rem;
    margin: 0rem auto;
    position: relative;
    display: flex;
    height: 100%;
    height: ${({ theme }) => theme.util.pixelToRem(1144)};
    max-height: 80vh;
    overflow-y: auto;
    padding-inline: 2rem;
    border-radius: ${({ theme }) => theme.borderRadius.rounded};
    box-shadow: 0 4px 4px 0px rgba(0, 0, 0, 0.05);
    border: ${({ theme }) => theme.borders.s.solid(ColorsEnum.grey, ColorsToneEnum.light)};

    .page-menu {
      height: 100%;
      border-right: ${({ theme }) => theme.borders.s.solid(ColorsEnum.grey, ColorsToneEnum.light)};
      padding-block: 1.875rem;
      padding-right: 1.875rem;
      color: ${({ theme }) => theme.colors.grey.darker};

      details {
        margin-bottom: 1rem;
        ${({ theme }) => theme.typography.s.normal};

        > * {
          transition: 200ms ease-in;
        }

        > summary {
          list-style-type: none;
          ${({ theme }) => theme.typography.m.normal};
          display: flex;
          justify-content: space-between;
          gap: 1rem;
          align-items: center;
          white-space: nowrap;
        }

        > ul {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;

          a {
            padding-block: 0.5rem;
            padding-inline: 1.5rem;
            display: block;

            &.active {
              background: #eaf9f7;
              border-radius: 10px;
            }
          }
        }
      }

      details > summary::-webkit-details-marker {
        display: none;
      }

      details[open] > summary {
        margin-bottom: 0.5rem;

        span {
          display: inline-block;
          transform: rotate(90deg);
        }
      }
    }

    .page-content {
      padding: 2.25rem;
      padding-top: 1.5rem;
      width: 100%;
      height: 100%;
      overflow: hidden;

      .header {
        color: ${({ theme }) => theme.colors.primary.darker};
        margin-bottom: ${({ theme }) => theme.util.pixelToRem(25)};

        > h2 {
          ${({ theme }) => theme.typography.h5.normal};
          font-weight: ${({ theme }) => theme.fontWeight.bold};
          white-space: nowrap;
        }

        select {
          width: 12rem;
          background-color: ${({ theme }) => theme.colors.grey.lightest};
          border: 1px solid ${({ theme }) => theme.colors.primary.darker};
          color: ${({ theme }) => theme.colors.primary.darker};
        }
      }
    }
  }
`
